/**
 * Z.AI GLM provider adapter for ludicrous-gateway
 * Connects to Z.AI's Anthropic-compatible API
 */

import { BaseProvider } from "./base-provider.js";
import type {
  ProviderRequestContext,
  StreamHandler,
} from "../types/providers.js";
import type { MessagesRequest, MessagesResponse } from "../types/messages.js";
import type { ProviderConfig, ApiKeyConfig } from "../types/config.js";

/**
 * Rate limit error for Z.AI API
 */
export class ZaiRateLimitError extends Error {
  readonly retryAfterSeconds: number | undefined;

  constructor(message: string, retryAfterSeconds?: number) {
    super(message);
    this.name = "ZaiRateLimitError";
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/**
 * Z.AI API provider
 * Uses Anthropic-compatible API format
 */
export class ZaiProvider extends BaseProvider {
  private apiKeys: ApiKeyConfig[];
  private currentKeyIndex: number = 0;

  constructor(config: ProviderConfig) {
    super(config);
    this.apiKeys = config.keys ?? [];

    if (this.apiKeys.length === 0) {
      this.logger.warn("No API keys configured for Z.AI provider");
    }
  }

  protected getDefaultBaseUrl(): string {
    return "https://api.z.ai/api/anthropic";
  }

  /**
   * Get the next API key (round-robin)
   */
  private getNextApiKey(): string | null {
    if (this.apiKeys.length === 0) {
      return null;
    }

    const key = this.apiKeys[this.currentKeyIndex];
    this.currentKeyIndex = (this.currentKeyIndex + 1) % this.apiKeys.length;
    return key?.key ?? null;
  }

  /**
   * Build headers for Z.AI API
   * Z.AI accepts both ANTHROPIC_AUTH_TOKEN and x-api-key headers
   */
  protected override buildHeaders(
    context: ProviderRequestContext
  ): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      "anthropic-version": "2023-06-01",
    };

    const apiKey = this.getNextApiKey();
    if (apiKey) {
      // Z.AI supports both header formats - use both for maximum compatibility
      headers["x-api-key"] = apiKey;
      headers["ANTHROPIC_AUTH_TOKEN"] = apiKey;
    } else {
      // Fall back to original request's API key
      const originalApiKey =
        context.headers["x-api-key"] ||
        context.headers["ANTHROPIC_AUTH_TOKEN"] ||
        context.headers["anthropic_auth_token"];
      if (originalApiKey) {
        headers["x-api-key"] = originalApiKey;
        headers["ANTHROPIC_AUTH_TOKEN"] = originalApiKey;
      }
    }

    return headers;
  }

  /**
   * Send a non-streaming request to Z.AI
   */
  async sendRequest(
    request: MessagesRequest,
    context: ProviderRequestContext
  ): Promise<MessagesResponse> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/messages`;
      const headers = this.buildHeaders(context);

      const transformedRequest = {
        ...request,
        model: context.resolvedModel,
        stream: false,
      };

      this.logger.debug("Sending request to Z.AI", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(transformedRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        const status = response.status;

        // Handle rate limiting specifically
        if (status === 429) {
          const retryAfter = response.headers.get("retry-after");
          this.logger.warn("Z.AI rate limit hit", {
            retryAfter,
            model: context.resolvedModel,
          });
          throw new ZaiRateLimitError(
            `Z.AI rate limit exceeded${retryAfter ? ` - retry after ${retryAfter}s` : ""}`,
            retryAfter ? parseInt(retryAfter, 10) : undefined
          );
        }

        // Handle other common errors
        if (status === 401 || status === 403) {
          throw new Error(`Z.AI authentication error: ${status} - ${errorText}`);
        }

        if (status >= 500) {
          throw new Error(`Z.AI server error: ${status} - ${errorText}`);
        }

        throw new Error(`Z.AI API error: ${status} - ${errorText}`);
      }

      const data = await response.json();
      const latencyMs = Date.now() - startTime;

      this.recordRequest(true, latencyMs);
      this.logger.debug("Z.AI request successful", { latencyMs });

      return this.transformResponse(data);
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      this.logger.error("Z.AI request failed", {
        error: error instanceof Error ? error.message : "Unknown error",
        latencyMs,
      });

      throw error;
    }
  }

  /**
   * Send a streaming request to Z.AI
   */
  async sendStreamingRequest(
    request: MessagesRequest,
    context: ProviderRequestContext,
    handler: StreamHandler
  ): Promise<void> {
    const startTime = Date.now();

    try {
      const url = `${this.baseUrl}/v1/messages`;
      const headers = this.buildHeaders(context);
      headers["Accept"] = "text/event-stream";

      const transformedRequest = {
        ...request,
        model: context.resolvedModel,
        stream: true,
      };

      this.logger.debug("Starting streaming request to Z.AI", {
        url,
        model: context.resolvedModel,
      });

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        context.timeoutMs
      );

      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(transformedRequest),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorText = await response.text();
        const status = response.status;

        // Handle rate limiting specifically
        if (status === 429) {
          const retryAfter = response.headers.get("retry-after");
          this.logger.warn("Z.AI streaming rate limit hit", {
            retryAfter,
            model: context.resolvedModel,
          });
          throw new ZaiRateLimitError(
            `Z.AI rate limit exceeded${retryAfter ? ` - retry after ${retryAfter}s` : ""}`,
            retryAfter ? parseInt(retryAfter, 10) : undefined
          );
        }

        // Handle other common errors
        if (status === 401 || status === 403) {
          throw new Error(`Z.AI authentication error: ${status} - ${errorText}`);
        }

        if (status >= 500) {
          throw new Error(`Z.AI server error: ${status} - ${errorText}`);
        }

        throw new Error(`Z.AI API error: ${status} - ${errorText}`);
      }

      if (!response.body) {
        throw new Error("No response body for streaming request");
      }

      // Process SSE stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            const eventType = line.slice(7);
            handler.onEvent(eventType, "");
          } else if (line.startsWith("data: ")) {
            const data = line.slice(6);
            handler.onEvent("data", data);
          }
        }
      }

      const latencyMs = Date.now() - startTime;
      this.recordRequest(true, latencyMs);
      handler.onComplete();

      this.logger.debug("Z.AI streaming request completed", { latencyMs });
    } catch (error) {
      const latencyMs = Date.now() - startTime;
      this.recordRequest(false, latencyMs);

      const err = error instanceof Error ? error : new Error("Unknown error");
      this.logger.error("Z.AI streaming request failed", {
        error: err.message,
        latencyMs,
      });

      handler.onError(err);
    }
  }
}

/**
 * Create a Z.AI provider from config
 */
export function createZaiProvider(config: ProviderConfig): ZaiProvider {
  return new ZaiProvider(config);
}
